import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { 
  IoRestaurantOutline, 
  IoPulseOutline, 
  IoHappyOutline, 
  IoScaleOutline, 
  IoMoonOutline, 
  IoWaterOutline, 
  IoRadioOutline, 
  IoClose 
} from "react-icons/io5";

import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  TabList,
  TabItem,
  FormGroup,
  SecondaryButton,
  PrimaryButton
} from "./styles";

const categories = [
  { id: "food", label: "Food", icon: <IoRestaurantOutline /> },
  { id: "activity", label: "Activity", icon: <IoPulseOutline /> },
  { id: "mood", label: "Mood", icon: <IoHappyOutline /> },
  { id: "measurement", label: "Measurement", icon: <IoScaleOutline /> },
  { id: "sleep", label: "Sleep", icon: <IoMoonOutline /> },
  { id: "water", label: "Water", icon: <IoWaterOutline /> },
  { id: "bowel movement", label: "Bowel Movement", icon: <IoRadioOutline /> },
];

const AddEntryModal = ({ onClose, onSave, editingData }) => {
  const { config } = useSelector((state) => state.Lifestyle);

  const visibleCategories = useMemo(() => {
    return categories.filter((cat) => {
      if (config && Object.keys(config).length > 0) {
        // Robust check for falsey values representing disabled categories
        const val = config[cat.label];
        if (val === false || val === "false" || val === 0 || val === "0") {
          return false;
        }
      }
      return true; // Default to visible if config isn't available
    });
  }, [config]);

  const [activeTab, setActiveTab] = useState(editingData?.type || (visibleCategories[0]?.id || "food"));
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    food: editingData?.type === "food" ? (editingData.data.data || {}) : { meal_type: "", description: "" },
    activity: editingData?.type === "activity" ? (editingData.data.data || {}) : { description: "", duration: "", calories: "" },
    mood: editingData?.type === "mood" ? (editingData.data.data || {}) : { mood: "", summary: "" },
    measurement: editingData?.type === "measurement" ? (editingData.data.data || {}) : { height: "", weight: "" },
    sleep: editingData?.type === "sleep" ? (editingData.data.data || {}) : { hours: "", quality: "" },
    water: editingData?.type === "water" ? (editingData.data.data || {}) : { amount: "" },
    "bowel movement": editingData?.type === "bowel movement" ? (editingData.data.data || {}) : { type: "", notes: "" }
  });

  useEffect(() => {
    // If the active tab becomes hidden, switch to the first available category
    if (!editingData && visibleCategories.length > 0) {
      const isCurrentTabVisible = visibleCategories.some(c => c.id === activeTab);
      if (!isCurrentTabVisible) {
        setActiveTab(visibleCategories[0].id);
      }
    }
  }, [config, activeTab, editingData, visibleCategories]);

  const validateForm = () => {
    const currentData = formData[activeTab];
    switch (activeTab) {
      case "food":
        return currentData.meal_type && currentData.description;
      case "activity":
        return currentData.description && currentData.duration;
      case "mood":
        return currentData.mood && currentData.summary;
      case "measurement":
        return currentData.height && currentData.weight;
      case "sleep":
        return currentData.hours && currentData.quality;
      case "water":
        return currentData.amount;
      case "bowel movement":
        return currentData.type;
      default:
        return true;
    }
  };

  const handleChange = (category, field, value) => {
    setError("");
    setFormData(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  const renderForm = () => {
    switch (activeTab) {
      case "food":
        return (
          <>
            <FormGroup>
              <label>Meal Type</label>
              <input 
                type="text" 
                placeholder="e.g. Breakfast, Lunch" 
                value={formData.food.meal_type}
                onChange={(e) => handleChange("food", "meal_type", e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <label>Description</label>
              <textarea 
                placeholder="What did you eat?"
                value={formData.food.description}
                onChange={(e) => handleChange("food", "description", e.target.value)}
              />
            </FormGroup>
          </>
        );
      case "activity":
        return (
          <>
            <FormGroup>
              <label>Describe your activity</label>
              <textarea 
                placeholder="e.g. Running, Yoga"
                value={formData.activity.description}
                onChange={(e) => handleChange("activity", "description", e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <label>Duration</label>
              <input 
                type="text" 
                placeholder="e.g. 30 mins"
                value={formData.activity.duration}
                onChange={(e) => handleChange("activity", "duration", e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <label>Calories Burned</label>
              <input 
                type="text" 
                placeholder="e.g. 200 kcal"
                value={formData.activity.calories}
                onChange={(e) => handleChange("activity", "calories", e.target.value)}
              />
            </FormGroup>
          </>
        );
      case "mood":
        return (
          <>
            <FormGroup>
              <label>Mood</label>
              <input 
                type="text" 
                placeholder="e.g. Happy, Stressed"
                value={formData.mood.mood}
                onChange={(e) => handleChange("mood", "mood", e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <label>Summary</label>
              <textarea 
                placeholder="How are you feeling?"
                value={formData.mood.summary}
                onChange={(e) => handleChange("mood", "summary", e.target.value)}
              />
            </FormGroup>
          </>
        );
      case "measurement":
        return (
          <>
            <FormGroup>
              <label>Height</label>
              <input 
                type="text" 
                placeholder="e.g. 175 cm"
                value={formData.measurement.height}
                onChange={(e) => handleChange("measurement", "height", e.target.value)}
              />
            </FormGroup>
            <FormGroup>
              <label>Weight</label>
              <input 
                type="text" 
                placeholder="e.g. 70 kg"
                value={formData.measurement.weight}
                onChange={(e) => handleChange("measurement", "weight", e.target.value)}
              />
            </FormGroup>
          </>
        );
      case "sleep":
        return (
            <>
              <FormGroup>
                <label>Duration</label>
                <input 
                  type="text" 
                  placeholder="e.g. 8 hours"
                  value={formData.sleep.hours}
                  onChange={(e) => handleChange("sleep", "hours", e.target.value)}
                />
              </FormGroup>
              <FormGroup>
                <label>Quality</label>
                <input 
                  type="text" 
                  placeholder="e.g. Good, Restless"
                  value={formData.sleep.quality}
                  onChange={(e) => handleChange("sleep", "quality", e.target.value)}
                />
              </FormGroup>
            </>
          );
      case "water":
        return (
            <FormGroup>
                <label>Amount</label>
                <input 
                    type="text" 
                    placeholder="e.g. 2 Liters"
                    value={formData.water.amount}
                    onChange={(e) => handleChange("water", "amount", e.target.value)}
                />
            </FormGroup>
        );
      case "bowel":
      case "bowel movement":
        return (
            <>
                <FormGroup>
                    <label>Type</label>
                    <input 
                        type="text" 
                        placeholder="e.g. Normal"
                        value={formData["bowel movement"]?.type || ""}
                        onChange={(e) => handleChange("bowel movement", "type", e.target.value)}
                    />
                </FormGroup>
                <FormGroup>
                    <label>Notes</label>
                    <textarea 
                        placeholder="Any observations?"
                        value={formData["bowel movement"]?.notes || ""}
                        onChange={(e) => handleChange("bowel movement", "notes", e.target.value)}
                    />
                </FormGroup>
            </>
        );
      default:
        return null;
    }
  };

  const handleSave = () => {
    if (!validateForm()) {
        setError("Please complete all required fields before saving");
        return;
    }
    onSave({
        id: editingData?.id,
        type: activeTab,
        data: formData[activeTab],
        timestamp: editingData?.timestamp || new Date().toISOString()
    });
    onClose();
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <h2>{editingData ? "Edit Entry" : "Add Entry"}</h2>
          <button onClick={onClose}><IoClose /></button>
        </ModalHeader>
        <ModalBody>
          <TabList>
            {visibleCategories.map((cat) => (
              <TabItem 
                key={cat.id} 
                active={activeTab === cat.id}
                onClick={() => {
                  if (!editingData) {
                    setActiveTab(cat.id);
                    setError("");
                  }
                }}
                style={{ opacity: editingData && activeTab !== cat.id ? 0.5 : 1, cursor: editingData ? "default" : "pointer" }}
              >
                {cat.icon}
                <span>{cat.label}</span>
              </TabItem>
            ))}
          </TabList>
          {error && (
            <div style={{ color: "#ef4444", fontSize: "14px", marginBottom: "16px", background: "#fef2f2", padding: "10px", borderRadius: "8px", border: "1px solid #fee2e2" }}>
              {error}
            </div>
          )}
          {renderForm()}
        </ModalBody>
        <ModalFooter>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleSave}>{editingData ? "Update Entry" : "Save"}</PrimaryButton>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AddEntryModal;
